import React, { useEffect, useMemo, useState } from 'react'

import cn from 'classnames'
import { RepeatIcon } from 'lucide-react'
import ReactLoading from 'react-loading'

import { ChevronDownIcon } from '@heroicons/react/24/outline'

import OrderHistoryItemRow from '@src/components/OrderHistoryTable/OrderHistoryItem'
import Pagination from '@src/components/OrderHistoryTable/Pagination'
import { ChainId } from '@src/configs/chainInfo'
import { useRepeatTrade } from '@src/hooks/useRepeatTrade'
import { OrderStatus } from '@src/types/order'
import { IToken } from '@src/types/token'
import { isFeatureEnabled } from '@src/utils/environment'

export type OrderItemTableRow = {
    sellToken: IToken
    buyToken: IToken
    sellQuantity: string | number
    buyQuantity: string | number
    originChainId: string
    destChainId: string
    originChainTxHash: string
    destChainTxHash: string | null
    order_type: 'order_type' | 'cctp'
    status?: OrderStatus
    created_at: string
    filled_at: string | null
    expires_at: string | null
    completed: boolean
    eta: number | null
    walletAddress: string
}

type Props = {
    data: OrderItemTableRow[]
    loadingStatus: boolean
    search: string
}

type Order = 'asc' | 'desc'

function descendingComparator(a: OrderItemTableRow, b: OrderItemTableRow, orderBy: keyof OrderItemTableRow) {
    const aValue = a?.[orderBy]
    const bValue = b?.[orderBy]

    if (aValue === null || aValue === undefined) return 1
    if (bValue === null || bValue === undefined) return -1
    if (bValue < aValue) return -1
    if (bValue > aValue) return 1
    return 0
}

function getComparator(order: Order, orderBy: keyof OrderItemTableRow) {
    return order === 'desc'
        ? (a: OrderItemTableRow, b: OrderItemTableRow) => descendingComparator(a, b, orderBy)
        : (a: OrderItemTableRow, b: OrderItemTableRow) => -descendingComparator(a, b, orderBy)
}

function stableSort(array: OrderItemTableRow[], comparator: (a: OrderItemTableRow, b: OrderItemTableRow) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [OrderItemTableRow, number])
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0])
        if (order !== 0) {
            return order
        }
        return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
}

// Add CCTP chains confirmation times - adjust chain IDs to match your system
const CCTP_CONFIRMATION_TIMES: Record<number, { blocks: number; time: string }> = {
    [ChainId.Ethereum]: { blocks: 65, time: '~13 minutes' }, // Ethereum
    [ChainId.Avalanche]: { blocks: 1, time: '~20 seconds' }, // Avalanche
    [ChainId.Optimism]: { blocks: 65, time: '~13 minutes' }, // OP Mainnet
    [ChainId.Arbitrum]: { blocks: 65, time: '~13 minutes' }, // Arbitrum
    [ChainId.Bsc]: { blocks: 1, time: '~20 seconds' }, // Noble
    [ChainId.Base]: { blocks: 65, time: '~13 minutes' }, // Base
    [ChainId.Polygon]: { blocks: 200, time: '~8 minutes' }, // Polygon PoS
    [ChainId.Solana]: { blocks: 32, time: '~25 seconds' }, // Solana
    [ChainId.Unichain]: { blocks: 65, time: '~13 minutes' }, // Unichain
}

const OrderHistoryTable: React.FC<Props> = ({ data, loadingStatus, search }) => {
    const [page, setPage] = useState(0)
    const [statusSort, setStatusSort] = useState<'none' | 'completed' | 'pending' | 'expired'>('none')
    const ROWS_PER_PAGE = 10
    const { repeatTrade } = useRepeatTrade()

    // Process data in sequence: filter -> sort -> paginate
    const processedData = useMemo(() => {
        // 1. Filter data based on search
        const filteredData = search
            ? data.filter(
                  item =>
                      item.sellToken.symbol.toLowerCase().includes(search.toLowerCase()) ||
                      item.buyToken.symbol.toLowerCase().includes(search.toLowerCase()) ||
                      item.originChainTxHash.toLowerCase().includes(search.toLowerCase()) ||
                      item.destChainTxHash?.toLowerCase().includes(search.toLowerCase())
              )
            : data

        // 2. Sort by status if needed
        let sortedData = [...filteredData]
        if (statusSort !== 'none') {
            sortedData = filteredData.filter(item => {
                const isPending = !item.destChainTxHash
                if (!isPending) return statusSort === 'completed'

                // Check expiration for pending orders
                const expirationTime = item.expires_at ? new Date(item.expires_at).getTime() : 0
                const currentTime = new Date().getTime()
                const isExpired = expirationTime && currentTime > expirationTime

                if (statusSort === 'expired') return isExpired
                if (statusSort === 'pending') return !isExpired
                return false
            })
        }

        // 3. Sort by timestamp
        return stableSort(sortedData, getComparator('desc', 'sellToken'))
    }, [data, search, statusSort])

    // Calculate total pages based on processed data length
    const totalPages = Math.ceil(processedData.length / ROWS_PER_PAGE)

    useEffect(() => {
        if (page >= totalPages) {
            setPage(0)
        }
    }, [processedData.length])

    // Get current page's rows - ensure exactly 10 items
    const visibleRows = useMemo(() => {
        const start = page * ROWS_PER_PAGE
        const end = Math.min(start + ROWS_PER_PAGE, processedData.length)
        return processedData.slice(start, end)
    }, [processedData, page])

    const handlePageChange = (newPage: number) => {
        const maxPage = Math.max(0, Math.ceil(processedData.length / ROWS_PER_PAGE) - 1)
        const validPage = Math.min(Math.max(0, newPage), maxPage)
        setPage(validPage)
    }

    const handleStatusSort = () => {
        setStatusSort(current => {
            if (current === 'none') return 'completed'
            if (current === 'completed') return 'pending'
            if (current === 'pending') return 'expired'
            return 'none'
        })
        setPage(0) // Reset to first page when sorting changes
    }

    return (
        <div className='flex h-full w-full flex-col rounded-lg bg-[#141618]'>
            <div className='min-h-0 flex-1'>
                {/* Desktop Table View */}
                <div className='hidden md:block'>
                    <table className='w-full'>
                        <thead className='sticky top-0 z-30 bg-[#141618]'>
                            <tr className='border-b border-gray-700/50'>
                                <th
                                    onClick={handleStatusSort}
                                    className='group cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400 hover:text-white'
                                >
                                    <div className='flex items-center space-x-1'>
                                        <span>Status</span>
                                        <ChevronDownIcon
                                            className={cn(
                                                'h-4 w-4 transition-transform duration-200',
                                                statusSort === 'none' ? 'rotate-0' : 'rotate-180'
                                            )}
                                        />
                                        {statusSort !== 'none' && (
                                            <span className='text-xs text-gray-500'>
                                                (
                                                {statusSort === 'completed'
                                                    ? 'Completed'
                                                    : statusSort === 'expired'
                                                      ? 'Expired'
                                                      : 'Pending'}
                                                )
                                            </span>
                                        )}
                                    </div>
                                </th>
                                <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400'>
                                    From
                                </th>
                                <th className='w-8 py-3'></th>
                                <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400'>
                                    To
                                </th>
                                <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400'>
                                    Origin Chain Tx
                                </th>
                                <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400'>
                                    Destination Chain Tx
                                </th>
                                <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400'>
                                    Created
                                </th>
                                <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400'>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-700/50'>
                            {loadingStatus && (
                                <tr>
                                    <td colSpan={8} className='py-8'>
                                        <ReactLoading type='spin' height={25} width={25} className='mx-auto' />
                                    </td>
                                </tr>
                            )}
                            {processedData.length === 0 && !loadingStatus && search !== '' && (
                                <tr>
                                    <td colSpan={8} className='py-8 text-center text-gray-400'>
                                        No orders found for the search term.
                                    </td>
                                </tr>
                            )}
                            {!loadingStatus && processedData.length === 0 && search === '' && (
                                <tr>
                                    <td colSpan={8} className='py-8 text-center text-gray-400'>
                                        No order history found.
                                    </td>
                                </tr>
                            )}
                            {visibleRows.length > 0 &&
                                visibleRows.map((item: OrderItemTableRow) => {
                                    const isPending = !item.destChainTxHash
                                    let status = OrderStatus.COMPLETED

                                    if (isPending) {
                                        // Check if the order is expired
                                        const expirationTime = item.expires_at ? new Date(item.expires_at).getTime() : 0
                                        const currentTime = new Date().getTime()
                                        status =
                                            expirationTime && currentTime > expirationTime
                                                ? ('expired' as OrderStatus)
                                                : OrderStatus.PENDING
                                    }

                                    const tooltipContent =
                                        isPending && !isFeatureEnabled('USE_TESTNETS_ONLY')
                                            ? item.order_type === 'cctp'
                                                ? `CCTP transfers require ${CCTP_CONFIRMATION_TIMES[+item.destChainId]?.blocks || 'multiple'} block confirmations (${CCTP_CONFIRMATION_TIMES[+item.destChainId]?.time || 'varying'} average wait time).`
                                                : 'Transaction is pending confirmation on the destination chain.'
                                            : ''

                                    return (
                                        <tr
                                            key={`order-table-row-${item.originChainTxHash}`}
                                            className='group transition-colors hover:bg-gray-800/30'
                                        >
                                            <td className='px-4 py-4'>
                                                <span
                                                    className={cn(
                                                        'inline-flex items-center rounded-full px-4 py-1 text-sm font-medium',
                                                        status === OrderStatus.PENDING
                                                            ? 'bg-purple-400/10 text-purple-400'
                                                            : status === 'expired'
                                                              ? 'bg-red-400/10 text-red-400'
                                                              : 'bg-green-400/10 text-green-400'
                                                    )}
                                                >
                                                    {status === OrderStatus.PENDING ? (
                                                        <div className='flex items-center space-x-2'>
                                                            <svg className='h-3 w-3 animate-spin' viewBox='0 0 24 24'>
                                                                <circle
                                                                    className='opacity-25'
                                                                    cx='12'
                                                                    cy='12'
                                                                    r='10'
                                                                    stroke='currentColor'
                                                                    strokeWidth='4'
                                                                    fill='none'
                                                                />
                                                                <path
                                                                    className='opacity-75'
                                                                    fill='currentColor'
                                                                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                                                />
                                                            </svg>
                                                            <span>Pending</span>
                                                        </div>
                                                    ) : status === 'expired' ? (
                                                        'Expired'
                                                    ) : (
                                                        'Completed'
                                                    )}
                                                </span>
                                            </td>
                                            <OrderHistoryItemRow
                                                item={{ ...item, status }}
                                                isPending={isPending}
                                                tooltipContent={tooltipContent}
                                            />
                                        </tr>
                                    )
                                })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View */}
                <div className='md:hidden'>
                    {loadingStatus && (
                        <div className='flex justify-center py-8'>
                            <ReactLoading type='spin' height={25} width={25} />
                        </div>
                    )}

                    {processedData.length === 0 && !loadingStatus && search !== '' && (
                        <div className='py-8 text-center text-gray-400'>No orders found for the search term.</div>
                    )}

                    {!loadingStatus && processedData.length === 0 && search === '' && (
                        <div className='py-8 text-center text-gray-400'>No order history found.</div>
                    )}

                    {visibleRows.length > 0 && (
                        <div className='space-y-4 px-0 py-4'>
                            {/* Status Filter for Mobile */}
                            <div className='mb-4 ml-4'>
                                <button
                                    onClick={handleStatusSort}
                                    className='flex items-center space-x-2 rounded-md bg-gray-800 px-3 py-2 text-sm'
                                >
                                    <span>Filter by Status</span>
                                    <ChevronDownIcon
                                        className={cn(
                                            'h-4 w-4 transition-transform duration-200',
                                            statusSort === 'none' ? 'rotate-0' : 'rotate-180'
                                        )}
                                    />
                                    {statusSort !== 'none' && (
                                        <span className='text-xs text-gray-400'>
                                            (
                                            {statusSort === 'completed'
                                                ? 'Completed'
                                                : statusSort === 'expired'
                                                  ? 'Expired'
                                                  : 'Pending'}
                                            )
                                        </span>
                                    )}
                                </button>
                            </div>

                            {visibleRows.map((item: OrderItemTableRow) => {
                                const isPending = !item.destChainTxHash
                                let status = OrderStatus.COMPLETED

                                if (isPending) {
                                    // Check if the order is expired
                                    const expirationTime = item.expires_at ? new Date(item.expires_at).getTime() : 0
                                    const currentTime = new Date().getTime()
                                    status =
                                        expirationTime && currentTime > expirationTime
                                            ? ('expired' as OrderStatus)
                                            : OrderStatus.PENDING
                                }

                                // Format the created date
                                const createdDate = new Date(item.created_at)
                                const formattedDate = createdDate.toLocaleDateString()
                                const formattedTime = createdDate.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })

                                return (
                                    <div
                                        key={`order-card-${item.originChainTxHash}`}
                                        className='w-full overflow-hidden border-b border-gray-700/50'
                                    >
                                        {/* Card Header with Status and Date */}
                                        <div className='flex items-center justify-between px-4 py-2.5'>
                                            <span
                                                className={cn(
                                                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                                                    status === OrderStatus.PENDING
                                                        ? 'bg-purple-400/10 text-purple-400'
                                                        : status === 'expired'
                                                          ? 'bg-red-400/10 text-red-400'
                                                          : 'bg-green-400/10 text-green-400'
                                                )}
                                            >
                                                {status === OrderStatus.PENDING ? (
                                                    <div className='flex items-center space-x-1.5'>
                                                        <svg className='h-2.5 w-2.5 animate-spin' viewBox='0 0 24 24'>
                                                            <circle
                                                                className='opacity-25'
                                                                cx='12'
                                                                cy='12'
                                                                r='10'
                                                                stroke='currentColor'
                                                                strokeWidth='4'
                                                                fill='none'
                                                            />
                                                            <path
                                                                className='opacity-75'
                                                                fill='currentColor'
                                                                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                                            />
                                                        </svg>
                                                        <span>Pending</span>
                                                    </div>
                                                ) : status === 'expired' ? (
                                                    'Expired'
                                                ) : (
                                                    'Completed'
                                                )}
                                            </span>
                                            <div className='flex items-center space-x-2'>
                                                <span className='text-xs text-gray-400'>
                                                    {formattedDate} {formattedTime}
                                                </span>
                                                <button
                                                    onClick={() => {
                                                        console.log('🔄 Mobile repeat button clicked', {
                                                            sellToken: item.sellToken.symbol,
                                                            buyToken: item.buyToken.symbol,
                                                            originChainId: item.originChainId,
                                                            destChainId: item.destChainId,
                                                        })
                                                        repeatTrade(item)
                                                    }}
                                                    className='flex h-5 w-5 items-center justify-center rounded-full bg-gray-700/30 text-gray-300 transition-colors hover:bg-gray-700/50 hover:text-white'
                                                    title='Repeat this trade'
                                                >
                                                    <RepeatIcon size={12} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div className='px-4 py-3'>
                                            {/* From/To Exchange - Simplified for mobile */}
                                            <div className='mb-3 flex w-full items-center justify-between'>
                                                <div className='flex items-center'>
                                                    <div className='relative h-7 w-7 flex-shrink-0'>
                                                        <img
                                                            src={item.sellToken.logoURI}
                                                            alt={item.sellToken.symbol}
                                                            className='h-full w-full rounded-full object-cover'
                                                        />
                                                        <div className='absolute bottom-0 right-0 h-3 w-3 overflow-hidden rounded-full border border-gray-800 bg-gray-800'>
                                                            <img
                                                                src={`/chain-icons/${item.originChainId}.svg`}
                                                                alt='Chain'
                                                                className='h-full w-full'
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='ml-2 flex flex-col'>
                                                        <span className='text-sm font-medium'>
                                                            {item.sellToken.symbol}
                                                        </span>
                                                        <span className='text-xs text-gray-400'>
                                                            {(
                                                                +item.sellQuantity /
                                                                10 ** item.sellToken.decimals
                                                            ).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className='mx-2 text-gray-500'>→</div>

                                                <div className='flex items-center'>
                                                    <div className='mr-2 flex flex-col items-end'>
                                                        <span className='text-sm font-medium'>
                                                            {item.buyToken.symbol}
                                                        </span>
                                                        <span className='text-xs text-gray-400'>
                                                            {(
                                                                +item.buyQuantity /
                                                                10 ** item.buyToken.decimals
                                                            ).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                                                        </span>
                                                    </div>
                                                    <div className='relative h-7 w-7 flex-shrink-0'>
                                                        <img
                                                            src={item.buyToken.logoURI}
                                                            alt={item.buyToken.symbol}
                                                            className='h-full w-full rounded-full object-cover'
                                                        />
                                                        <div className='absolute bottom-0 right-0 h-3 w-3 overflow-hidden rounded-full border border-gray-800 bg-gray-800'>
                                                            <img
                                                                src={`/chain-icons/${item.destChainId}.svg`}
                                                                alt='Chain'
                                                                className='h-full w-full'
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Transaction Details */}
                                            <div className='flex flex-col space-y-2 pb-2 text-sm'>
                                                <div className='flex items-center justify-between'>
                                                    <span className='text-xs font-medium text-gray-400'>
                                                        Origin Tx:
                                                    </span>
                                                    <a
                                                        href={`https://etherscan.io/tx/${item.originChainTxHash}`}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='truncate text-xs text-blue-400 hover:text-blue-300'
                                                    >
                                                        {item.originChainTxHash.substring(0, 8)}...
                                                        {item.originChainTxHash.substring(
                                                            item.originChainTxHash.length - 8
                                                        )}
                                                    </a>
                                                </div>

                                                <div className='flex items-center justify-between'>
                                                    <span className='text-xs font-medium text-gray-400'>Dest Tx:</span>
                                                    {item.destChainTxHash ? (
                                                        <a
                                                            href={`https://etherscan.io/tx/${item.destChainTxHash}`}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            className='truncate text-xs text-blue-400 hover:text-blue-300'
                                                        >
                                                            {item.destChainTxHash.substring(0, 8)}...
                                                            {item.destChainTxHash.substring(
                                                                item.destChainTxHash.length - 8
                                                            )}
                                                        </a>
                                                    ) : status === OrderStatus.PENDING ? (
                                                        <span className='text-xs text-yellow-400'>
                                                            Pending confirmation...
                                                        </span>
                                                    ) : (
                                                        <span className='text-xs text-red-400'>Failed</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
    )
}

export default OrderHistoryTable
